import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import {
    submissionPayloadUrl, useSubmission,
    type SubmissionResponseRow, type SubmissionStatus,
} from '@/api/edi';
import { useAuthStore } from '@/stores/auth';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { StatusBadge } from '@/components/ui/Badge';
import { ediSubmissionVariant, ediResponseVariant } from '@/lib/statusBadge';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
/*
 * Submission detail — one row per 837P. Shows envelope metadata, the response
 * timeline (999/277CA/835 arrivals with parsed summaries), and the per-claim
 * disposition table. The raw X12 payload is a separate protected download —
 * clicking it writes its own audit entry on the server.
 */

const STATUS_LABEL: Record<SubmissionStatus, string> = {
    pending_upload: 'Pending upload',
    uploaded:       'Uploaded',
    ack_accepted:   'Accepted (999)',
    ack_rejected:   'Rejected',
    partial_paid:   'Partially paid',
    paid:           'Paid',
    failed:         'Failed',
};

function fmtMoney(n: number): string {
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function fmtDateTime(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
}

export function BillingSubmissionDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const canView = useAuthStore((s) => s.hasPermission('view_billing'));
    const { data, isLoading, isError, error } = useSubmission(id);

    if (!canView) {
        return (
            <div className="p-6 max-w-[1100px] mx-auto">
                <p className="text-sm text-text-muted">You don't have permission to view claim submissions.</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="p-12 flex items-center justify-center text-text-subtle">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
        );
    }
    if (isError || !data) {
        return (
            <div className="p-6 max-w-[1100px] mx-auto">
                <Button variant="ghost" size="sm" onClick={() => navigate('/billing/submissions')}>
                    <ArrowLeft className="w-3.5 h-3.5" /> Back
                </Button>
                <p className="mt-4 text-sm text-error">
                    {extractErrorMessage(error, 'Submission not found.')}
                </p>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-[1400px] mx-auto">
            <div className="flex items-center gap-3 mb-4">
                <Button variant="ghost" size="sm" onClick={() => navigate('/billing/submissions')}>
                    <ArrowLeft className="w-3.5 h-3.5" /> Back
                </Button>
                <div>
                    <h1 className="font-display text-2xl font-bold text-text flex items-center gap-3">
                        <span className="font-mono text-base">{data.filename}</span>
                        {data.isTest && (
                            <span className="text-2xs uppercase tracking-wide text-warning-text">TEST</span>
                        )}
                    </h1>
                    <p className="text-xs text-text-subtle">
                        ICN <span className="font-mono">{data.interchangeControl}</span>
                        {data.groupControl ? <> · GCN <span className="font-mono">{data.groupControl}</span></> : null}
                    </p>
                </div>
                <div className="ml-auto">
                    <a
                        href={submissionPayloadUrl(data.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-surface-muted hover:bg-surface text-sm text-text border border-border"
                    >
                        <Download className="w-3.5 h-3.5" /> Download X12
                    </a>
                </div>
            </div>

            <div className="grid grid-cols-4 gap-3 mb-5">
                <Card className="p-3">
                    <div className="text-2xs uppercase tracking-wider text-text-subtle">Status</div>
                    <div className="mt-1">
                        <Badge variant={ediSubmissionVariant(data.status)}>
                            {STATUS_LABEL[data.status] || data.status}
                        </Badge>
                    </div>
                </Card>
                <Card className="p-3">
                    <div className="text-2xs uppercase tracking-wider text-text-subtle">Claims</div>
                    <div className="mt-1 text-lg font-bold tabular-nums">{data.claimCount}</div>
                </Card>
                <Card className="p-3">
                    <div className="text-2xs uppercase tracking-wider text-text-subtle">Billed</div>
                    <div className="mt-1 text-lg font-bold tabular-nums">{fmtMoney(data.totalBilled)}</div>
                </Card>
                <Card className="p-3">
                    <div className="text-2xs uppercase tracking-wider text-text-subtle">Uploaded</div>
                    <div className="mt-1 text-sm text-text">{fmtDateTime(data.uploadedAt)}</div>
                </Card>
            </div>

            {data.lastError && (
                <div className="mb-4 p-3 border border-error-text bg-error-soft rounded-md text-sm text-error-text">
                    <div className="font-medium mb-0.5">Last error</div>
                    <div className="text-xs whitespace-pre-wrap">{data.lastError}</div>
                </div>
            )}

            <h2 className="font-display text-lg font-bold text-text mb-2">Responses</h2>
            <Card className="p-0 overflow-hidden mb-5">
                {data.responses.length === 0 ? (
                    <div className="p-6 text-center text-text-subtle text-sm">
                        No responses received yet. 999 (syntactic ack) typically arrives within
                        minutes; 277CA (claim dispositions) within hours; 835 (remittance) after
                        adjudication.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                <th className="px-3 py-2 font-bold">Type</th>
                                <th className="px-3 py-2 font-bold">Received</th>
                                <th className="px-3 py-2 font-bold">Filename</th>
                                <th className="px-3 py-2 font-bold">Status</th>
                                <th className="px-3 py-2 font-bold">Summary</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.responses.map((r) => (
                                <tr key={r.id} className="border-b border-border last:border-b-0 align-top">
                                    <td className="px-3 py-2 font-mono text-xs">{r.type}</td>
                                    <td className="px-3 py-2 text-text-muted whitespace-nowrap">
                                        {fmtDateTime(r.receivedAt)}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs text-text-muted">{r.filename || '—'}</td>
                                    <td className="px-3 py-2">
                                        <Badge variant={ediResponseVariant(r.type, r.status)}>
                                            {r.status || '—'}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-text-muted">
                                        <ResponseSummary row={r} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            <h2 className="font-display text-lg font-bold text-text mb-2">Claims in this batch</h2>
            <Card className="p-0 overflow-hidden">
                {data.claims.length === 0 ? (
                    <div className="p-6 text-center text-text-subtle text-sm">No claims linked.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                <th className="px-3 py-2 font-bold">Claim #</th>
                                <th className="px-3 py-2 font-bold">Client</th>
                                <th className="px-3 py-2 font-bold">OA Trace</th>
                                <th className="px-3 py-2 font-bold text-right">Billed</th>
                                <th className="px-3 py-2 font-bold text-right">Paid</th>
                                <th className="px-3 py-2 font-bold">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.claims.map((c) => (
                                <tr key={c.id} className="border-b border-border last:border-b-0 hover:bg-surface-muted transition-colors">
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        <Link to={`/billing/${c.id}`} className="font-medium hover:underline">
                                            {c.displayId}
                                        </Link>
                                    </td>
                                    <td className="px-3 py-2">{c.client || '—'}</td>
                                    <td className="px-3 py-2 font-mono text-xs text-text-muted">
                                        {c.oaTraceNumber || '—'}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(c.billed)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums text-text-muted">
                                        {c.paid ? fmtMoney(c.paid) : '—'}
                                    </td>
                                    <td className="px-3 py-2">
                                        <StatusBadge status={c.status} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>
        </div>
    );
}

function ResponseSummary({ row }: { row: SubmissionResponseRow }) {
    const parsed = row.parsed || {};
    if (row.type === '999') {
        const errors = Array.isArray((parsed as { errors?: unknown[] }).errors) ? (parsed as { errors: unknown[] }).errors : [];
        return (
            <span>
                {errors.length === 0 ? 'Clean acknowledgment.' : `${errors.length} syntactic error${errors.length === 1 ? '' : 's'}.`}
            </span>
        );
    }
    if (row.type === '277CA') {
        const claims = Array.isArray((parsed as { claims?: unknown[] }).claims) ? (parsed as { claims: unknown[] }).claims : [];
        return <span>{claims.length} claim disposition{claims.length === 1 ? '' : 's'}.</span>;
    }
    if (row.type === '835') {
        const p = parsed as { payment?: { amount?: number }; claims?: unknown[] };
        const amt = p.payment?.amount;
        const count = Array.isArray(p.claims) ? p.claims.length : 0;
        return (
            <span>
                {typeof amt === 'number' ? fmtMoney(amt) : '—'} across {count} claim{count === 1 ? '' : 's'}.
            </span>
        );
    }
    return null;
}
